import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../AdminLayout.tsx';
import { adminFetch } from '../../../utils/adminFetch.ts';

interface User {
  _id: string;
  email: string;
  name?: string;
  googleId?: string;
  credits: number;
  createdAt?: string;
  lastLogin?: string;
}

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<'name' | 'email' | 'credits'>('email');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const users = await adminFetch<User[]>('/users');
        console.log("Users response:", users);
        setUsers(users);
      } catch (err: any) {
        console.error('Error fetching users:', err.message);
        setError(`Error: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUsers();
  }, []);

  // Handle user deletion
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      console.log(`Deleting user ${userId}`);
      
      // Backend returns 204 No Content on successful deletion
      await adminFetch(
        `/users/${userId}`,
        {
          method: 'DELETE'
        }
      );
      
      // If we get here, the delete was successful (otherwise an error would be thrown)
      console.log("User deleted successfully");
      // Remove user from the list
      setUsers(users.filter(user => user._id !== userId));
    } catch (err: any) {
      console.error('Error deleting user:', err.message);
      alert(`Error: ${err.message}`);
    }
  };

  // Sort users
  const sortedUsers = [...users].sort((a, b) => {
    let aValue = a[sortField] || '';
    let bValue = b[sortField] || '';
    
    // For string comparisons
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue) 
        : bValue.localeCompare(aValue);
    }
    
    // For number comparisons
    return sortDirection === 'asc' 
      ? (aValue as number) - (bValue as number) 
      : (bValue as number) - (aValue as number);
  });

  // Filter users by search term
  const filteredUsers = sortedUsers.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const usersPerPage = 10;
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  // Sort handler
  const handleSort = (field: 'name' | 'email' | 'credits') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <AdminLayout title="User Management">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center">
          <input
            type="text"
            placeholder="Search users..."
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
          />
        </div>
        <Link
          to="/admin/users/create"
          className="bg-upload-button-pressed text-white px-4 py-2 rounded-lg hover:bg-upload-button-hover hover:text-black transition-colors"
        >
          Create New User
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-base rounded-lg overflow-hidden">
              <thead className="bg-gray-base">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                    <button 
                      onClick={() => handleSort('name')}
                      className="flex items-center focus:outline-none"
                    >
                      Name
                      {sortField === 'name' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                    <button 
                      onClick={() => handleSort('email')}
                      className="flex items-center focus:outline-none"
                    >
                      Email
                      {sortField === 'email' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                    <button 
                      onClick={() => handleSort('credits')}
                      className="flex items-center focus:outline-none"
                    >
                      Credits
                      {sortField === 'credits' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-base">
                {paginatedUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-light">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium">{user.name || '(No name)'}</div>
                      {user.googleId && (
                        <div className="text-xs text-gray-dark">Google ID: {user.googleId}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{user.credits}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-3">
                        <Link
                          to={`/admin/users/${user._id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDeleteUser(user._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <nav className="flex items-center">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="mr-2 px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`mx-1 px-3 py-1 rounded ${
                      currentPage === index + 1
                        ? 'bg-upload-button-pressed text-white'
                        : 'border'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-2 px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default UsersList;
